import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { AgGrid } from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { GetComplianceClientsSliceDocument } from "~/graphql/supergraphHooks";
import { type GetComplianceClientsSliceQuery } from "~/graphql/supergraphTypes";
import {
  ComplianceClientFilters,
  ComplianceClientSort,
  RowItem,
  TextFilter,
} from "~/pages/Compliance/ComplianceClientsTable/types";

const getFilterVars = (
  filterModel: AgGrid.FilterModel | AgGrid.AdvancedFilterModel | null,
): ComplianceClientFilters[] | null => {
  if (!filterModel) {
    return null;
  }
  if (filterModel?.filterType) {
    throw Error("Handling for AgGrid.AdvancedFilterModel not implemented.");
  }
  const queryFilters: ComplianceClientFilters[] = [];
  Object.entries(filterModel ?? {}).forEach((filter) => {
    const [field, filterInfo] = filter;
    if (filterInfo.filterType == "set") {
      queryFilters.push({
        field: field,
        filterValues: filterInfo.values.map((value: unknown) => String(value)),
      });
    } else if (filterInfo.filterType == "text") {
      queryFilters.push({ field: field, filterValues: [(filterInfo as TextFilter).filter] });
    } else {
      throw Error("Unsupported filter type");
    }
  });
  return queryFilters;
};

const getSortVars = (sortItems: AgGrid.SortModelItem[]): ComplianceClientSort[] | null => {
  if (!sortItems) {
    return null;
  }
  const querySorts: ComplianceClientSort[] = [];
  sortItems.forEach((sort) => {
    querySorts.push({ field: sort.colId, sortAscending: sort.sort === "asc" });
  });
  return querySorts;
};

export const rowsFromQuery = (rows: RowItem[]) => rows;

export class ComplianceClientsGraphqlDataSource implements AgGrid.IServerSideDatasource {
  client: ApolloClient<NormalizedCacheObject>;

  constructor() {
    this.client = client;
  }

  public getRows(params: AgGrid.IServerSideGetRowsParams): void {
    const filters = getFilterVars(params.request.filterModel);
    const sorts = getSortVars(params.request.sortModel);
    const offset = params.request.startRow ?? 0;
    const limit = (params.request.endRow ?? 0) - offset;

    this.client
      .query<GetComplianceClientsSliceQuery>({
        query: GetComplianceClientsSliceDocument,
        variables: {
          offset: offset,
          limit: limit,
          filters: filters,
          sorts: sorts,
        },
        fetchPolicy: "no-cache",
      })
      .then((response) => {
        params.success({
          rowData: rowsFromQuery(response.data.complianceClientsSlice.rows),
          rowCount: response.data.complianceClientsSlice.filteredCount,
        });
      })
      .catch(() => {
        params.fail();
      });
  }
}
